import React from 'react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

const SITE_URL = 'https://keystone.cloudvoro.com';

const SECTION_TITLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 'clamp(22px, 2.6vw, 30px)',
  fontWeight: 700,
  color: PALETTE.navy,
  margin: '36px 0 14px',
  letterSpacing: '-0.015em',
};

const BODY = { fontSize: 15.5, lineHeight: 1.7, color: PALETTE.slate, margin: '0 0 14px' };

const Subhead = ({ children }) => (
  <h3 style={{
    fontSize: 13,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: PALETTE.magenta,
    margin: '22px 0 6px',
  }}>{children}</h3>
);

export default function SitePrivacy() {
  return (
    <SiteLayout>
      <SEO
        title="Privacy Policy, Keystone by CloudVoro"
        description="How Keystone by CloudVoro collects, stores and processes your data. Hosted in AWS Ireland (eu-west-1). GDPR-aligned. Privacy queries to privacy@cloudvoro.com."
        path="/site/privacy"
        keywords="CloudVoro privacy policy, Keystone GDPR, food software privacy, privacy contact CloudVoro, data protection Irish SaaS"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Privacy Policy',
          url: `${SITE_URL}/site/privacy`,
          description:
            'Privacy notice for Keystone by CloudVoro, GDPR-aligned data handling, EU hosting and Privacy Contact.',
        }}
      />

      <section style={{ background: PALETTE.cream, padding: '80px 32px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }} data-testid="privacy-policy">
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 12,
          }}>
            Compliance & trust
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(34px, 4.6vw, 54px)',
            fontWeight: 700, color: PALETTE.navy, lineHeight: 1.05,
            letterSpacing: '-0.025em', margin: '0 0 18px',
          }}>
            Privacy <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>policy.</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: PALETTE.slate, margin: 0, maxWidth: 720 }}>
            How CloudVoro (Ireland), the company behind Keystone, collects, stores and processes your
            personal data. This notice applies to keystone.cloudvoro.com and the Keystone product.
          </p>
          <p style={{ fontSize: 13, color: PALETTE.muted, marginTop: 12 }}>
            Last updated: 1 February 2026 · Effective immediately
          </p>
        </div>
      </section>

      <section style={{ background: PALETTE.paper, padding: '24px 32px 80px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          <h2 style={SECTION_TITLE}>1. Who we are</h2>
          <p style={BODY}>
            <strong>CloudVoro</strong> is an Irish-registered business. We build and operate <strong>Keystone</strong>,
            a production-operations platform for food manufacturers. CloudVoro is the data controller for personal data
            collected via this website. For Keystone customers, CloudVoro is a <em>data processor</em> acting on behalf of
            the customer organisation.
          </p>
          <p style={BODY}>
            <Subhead>Contact our DPO</Subhead>
            Designated Data Protection Officer: <a href="mailto:dpo@cloudvoro.com" style={{ color: PALETTE.magenta }}>dpo@cloudvoro.com</a><br />
            Postal: CloudVoro, Ireland (full address available on request)
          </p>

          <h2 style={SECTION_TITLE}>2. What we collect</h2>
          <Subhead>From website visitors</Subhead>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>IP address, user agent, referrer and visited paths (visitor logs, kept for 90 days)</li>
            <li>Form submissions on the contact page (name, company, email, phone, current system, message)</li>
            <li>Strictly necessary cookies for session state, no advertising or analytics trackers</li>
          </ul>
          <Subhead>From Keystone customers</Subhead>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>Account identifiers (name, email, role) for users granted access to a tenant</li>
            <li>Operational data your team enters into Keystone (batches, orders, dispatches, COAs, labels)</li>
            <li>Audit-log entries: who changed what, when, and from which IP, for the lifetime of the tenant</li>
          </ul>

          <h2 style={SECTION_TITLE}>3. How we use it</h2>
          <p style={BODY}>
            Personal data is processed on the lawful bases of <strong>legitimate interest</strong> (running the site,
            preventing abuse), <strong>contract</strong> (delivering Keystone to paying customers), and{' '}
            <strong>consent</strong> (where you explicitly opt in, e.g. for a reference call introduction).
          </p>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>Respond to enquiries you send via the contact form or email</li>
            <li>Operate, maintain and secure the Keystone product for paying customers</li>
            <li>Detect and investigate misuse (visitor logs, bot detection)</li>
            <li>Meet our legal, tax and regulatory obligations in Ireland and the EU</li>
          </ul>
          <p style={BODY}>We do not sell personal data. We do not run ad networks. We do not share data with brokers.</p>

          <h2 style={SECTION_TITLE}>4. Where it’s hosted</h2>
          <p style={BODY}>
            All production data is hosted on <strong>AWS Ireland (eu-west-1, Dublin)</strong>. Each Keystone tenant
            runs on a dedicated server with an isolated database. Data is encrypted at rest with KMS-managed keys and
            in transit with TLS 1.3.
          </p>

          <h2 style={SECTION_TITLE}>5. Who we share it with</h2>
          <p style={BODY}>
            Limited, named sub-processors with EU-or-equivalent data protection guarantees:
          </p>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><strong>AWS (Amazon Web Services EMEA SARL)</strong>, infrastructure hosting (Ireland)</li>
            <li><strong>CloudVoro SMTP relay</strong>, outbound email (mail.cloudvoro.com)</li>
            <li><strong>Stripe</strong>, payment processing for paid licences (only when applicable)</li>
            <li><strong>Calendly</strong>, scheduling discovery calls (only when you choose to book)</li>
          </ul>
          <p style={BODY}>A current sub-processor list is published at <a href="/legal/sub-processors" style={{ color: PALETTE.magenta }}>/legal/sub-processors</a>.</p>

          <h2 style={SECTION_TITLE}>6. How long we keep it</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>Visitor logs (IP, paths): 90 days, then purged</li>
            <li>Contact form submissions: 24 months from last contact, then archived or deleted</li>
            <li>Customer tenant data: for the life of the contract + 30 days post-termination (then irreversibly deleted unless legally required to retain)</li>
            <li>Encrypted backups: 30 days rolling, then overwritten</li>
          </ul>

          <h2 style={SECTION_TITLE}>7. Your rights (GDPR)</h2>
          <p style={BODY}>If you are in the EU/UK you have the right to:</p>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>Request a copy of the personal data we hold about you</li>
            <li>Ask us to correct inaccurate data, or delete data we are not legally required to retain</li>
            <li>Object to processing based on legitimate interest</li>
            <li>Withdraw consent at any time where consent is the basis</li>
            <li>Lodge a complaint with the Irish Data Protection Commission (<a href="https://www.dataprotection.ie/" style={{ color: PALETTE.magenta }}>dataprotection.ie</a>)</li>
          </ul>
          <p style={BODY}>
            Email <a href="mailto:privacy@cloudvoro.com" style={{ color: PALETTE.magenta }}>privacy@cloudvoro.com</a> and we’ll
            respond within 30 days.
          </p>

          <h2 style={SECTION_TITLE}>8. Cookies</h2>
          <p style={BODY}>
            We use <strong>strictly necessary cookies only</strong>, for session state and your cookie-consent
            choice. We do not run Google Analytics, Facebook Pixel, or any advertising tracker by default. If we
            ever add optional analytics, you will be asked to opt in first.
          </p>

          <h2 style={SECTION_TITLE}>9. Security posture</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li>Hosted in the EU / Ireland, no data leaves the EU without written consent; on-premise deployment available</li>
            <li>GDPR, internal Data Protection Lead (privacy@cloudvoro.com); founder is an ADPO Ireland member</li>
            <li>Multi-factor authentication for every user, rate limiting, brute-force lockout and reCAPTCHA bot protection</li>
            <li>Encrypted at rest (AES-256, KMS) and in transit (TLS 1.3)</li>
            <li>Security controls aligned to ISO/IEC 27001:2022 Annex A; tenant isolation and audit trails support customers' NIS2 obligations</li>
          </ul>

          <h2 style={SECTION_TITLE}>10. Changes to this notice</h2>
          <p style={BODY}>
            We will post any material changes here and update the “Last updated” date above. For
            customers, we will also email the primary tenant contact at least 30 days before substantive changes
            take effect.
          </p>

          <div style={{
            marginTop: 36, padding: '20px 24px', background: PALETTE.cream,
            border: `1px solid ${PALETTE.border}`,
          }}>
            <Subhead>Questions?</Subhead>
            <p style={{ ...BODY, margin: 0 }}>
              Email <a href="mailto:privacy@cloudvoro.com" style={{ color: PALETTE.magenta }}>privacy@cloudvoro.com</a>{' '}
              or reach the founder directly at{' '}
              <a href="mailto:hello@cloudvoro.com" style={{ color: PALETTE.magenta }}>hello@cloudvoro.com</a>.
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
